import React, { useState } from "react";
import { X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const labelClass =
  "mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500";

const selectClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const initialState = {
  name: "",
  legal_name: "",
  parent_group: "",
  headquarters_city: "",
  headquarters_country: "Portugal",
  official_url: "",
  company_size: "unknown",
  priority: "B",
  confidence_level: "medium",
  verification_status: "partially_verified",
  relationship_stage: "under_review",
  company_role: "general_contractor",
  speciality: "residential",
  capability: "general_construction",
  operates_in_portugal: true,
  description: "",
  why_relevant: "",
  contact_name: "",
  contact_job_title: "",
  contact_email: "",
  contact_phone: "",
  contact_verification_status: "unverified",
  contact_source_url: "",
};

export default function ConstructionCompanyForm({
  meta,
  saving,
  onCancel,
  onSave,
  t,
}) {
  const [form, setForm] = useState(initialState);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = (event) => {
    event.preventDefault();

    onSave({
      name: form.name.trim(),
      legal_name: form.legal_name.trim(),
      parent_group: form.parent_group.trim(),
      headquarters_city: form.headquarters_city.trim(),
      headquarters_country: form.headquarters_country.trim(),
      countries_of_operation: form.headquarters_country
        ? [form.headquarters_country]
        : [],
      official_url: form.official_url.trim(),
      company_size: form.company_size,
      priority: form.priority,
      confidence_level: form.confidence_level,
      verification_status: form.verification_status,
      relationship_stage: form.relationship_stage,
      company_roles: [form.company_role],
      specialities: [form.speciality],
      capabilities: [form.capability],
      operates_in_portugal: form.operates_in_portugal,
      description: form.description.trim(),
      why_relevant: form.why_relevant.trim(),
      contacts: [
        {
          name: form.contact_name.trim(),
          job_title: form.contact_job_title.trim(),
          email: form.contact_email.trim(),
          phone: form.contact_phone.trim(),
          source_url: (
            form.contact_source_url || form.official_url
          ).trim(),
          verification_status:
            form.contact_verification_status,
          is_primary: true,
        },
      ],
      sources: [
        {
          url: (
            form.contact_source_url || form.official_url
          ).trim(),
          title: "Official company source",
          source_type: "official_website",
          confidence_level: form.confidence_level,
        },
      ],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-8">
      <form
        onSubmit={submit}
        className="w-full max-w-5xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A77E35]">
              {t(
                "constructionPartners.form.eyebrow",
                "Verified partner profile"
              )}
            </p>
            <h2 className="mt-1 font-serif text-3xl text-[#0B2545]">
              {t(
                "constructionPartners.form.title",
                "Add construction company"
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-[#0B2545]"
            aria-label={t("common.close", "Close")}
          >
            <X size={22} />
          </button>
        </div>

        <div className="grid gap-8 p-6 lg:grid-cols-2">
          <section className="space-y-5">
            <h3 className="border-b border-slate-100 pb-3 font-serif text-xl text-[#0B2545]">
              {t(
                "constructionPartners.form.companyInformation",
                "Company information"
              )}
            </h3>

            <div>
              <label className={labelClass}>
                {t("constructionPartners.form.name", "Company name")} *
              </label>
              <Input
                required
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.legalName", "Legal name")}
                </label>
                <Input
                  value={form.legal_name}
                  onChange={(event) =>
                    update("legal_name", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.parentGroup", "Parent group")}
                </label>
                <Input
                  value={form.parent_group}
                  onChange={(event) =>
                    update("parent_group", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.country", "Country")} *
                </label>
                <Input
                  required
                  value={form.headquarters_country}
                  onChange={(event) =>
                    update("headquarters_country", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.city", "City")}
                </label>
                <Input
                  value={form.headquarters_city}
                  onChange={(event) =>
                    update("headquarters_city", event.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("constructionPartners.form.website", "Official website")} *
              </label>
              <Input
                required
                type="url"
                placeholder="https://"
                value={form.official_url}
                onChange={(event) =>
                  update("official_url", event.target.value)
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.role", "Company role")} *
                </label>
                <select
                  value={form.company_role}
                  onChange={(event) =>
                    update("company_role", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.company_roles || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.speciality", "Speciality")} *
                </label>
                <select
                  value={form.speciality}
                  onChange={(event) =>
                    update("speciality", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.specialities || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.capability", "Capability")} *
                </label>
                <select
                  value={form.capability}
                  onChange={(event) =>
                    update("capability", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.capabilities || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("constructionPartners.form.description", "Description")}
              </label>
              <textarea
                rows={4}
                value={form.description}
                onChange={(event) =>
                  update("description", event.target.value)
                }
                className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
              />
            </div>
          </section>

          <section className="space-y-5">
            <h3 className="border-b border-slate-100 pb-3 font-serif text-xl text-[#0B2545]">
              {t(
                "constructionPartners.form.professionalContact",
                "Professional contact"
              )}
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.contactName", "Contact name or department")} *
                </label>
                <Input
                  required
                  value={form.contact_name}
                  onChange={(event) =>
                    update("contact_name", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.jobTitle", "Position")}
                </label>
                <Input
                  value={form.contact_job_title}
                  onChange={(event) =>
                    update("contact_job_title", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.email", "Email")} *
                </label>
                <Input
                  required
                  type="email"
                  value={form.contact_email}
                  onChange={(event) =>
                    update("contact_email", event.target.value)
                  }
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.phone", "Telephone")} *
                </label>
                <Input
                  required
                  type="tel"
                  value={form.contact_phone}
                  onChange={(event) =>
                    update("contact_phone", event.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                {t("constructionPartners.form.contactSource", "Contact source URL")} *
              </label>
              <Input
                required
                type="url"
                placeholder="https://"
                value={form.contact_source_url}
                onChange={(event) =>
                  update("contact_source_url", event.target.value)
                }
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.priority", "Priority")}
                </label>
                <select
                  value={form.priority}
                  onChange={(event) =>
                    update("priority", event.target.value)
                  }
                  className={selectClass}
                >
                  {(meta?.priorities || []).map((item) => (
                    <option key={item} value={item}>
                      Priority {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  {t("constructionPartners.form.contactVerification", "Contact verification")}
                </label>
                <select
                  value={form.contact_verification_status}
                  onChange={(event) =>
                    update(
                      "contact_verification_status",
                      event.target.value
                    )
                  }
                  className={selectClass}
                >
                  {(meta?.contact_statuses || []).map((item) => (
                    <option key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-3 border border-slate-200 p-4 text-sm text-[#0B2545]">
              <input
                type="checkbox"
                checked={form.operates_in_portugal}
                onChange={(event) =>
                  update("operates_in_portugal", event.target.checked)
                }
              />
              {t(
                "constructionPartners.form.operatesPortugal",
                "Company operates in Portugal"
              )}
            </label>

            <div>
              <label className={labelClass}>
                {t(
                  "constructionPartners.form.relevance",
                  "Why this company is relevant"
                )}
              </label>
              <textarea
                rows={5}
                value={form.why_relevant}
                onChange={(event) =>
                  update("why_relevant", event.target.value)
                }
                className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
              />
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-5">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            className="rounded-none"
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="rounded-none bg-[#0B2545] hover:bg-[#12345e]"
          >
            {saving
              ? t("constructionPartners.form.saving", "Saving…")
              : t("constructionPartners.form.save", "Save company")}
          </Button>
        </div>
      </form>
    </div>
  );
}
